import { divRound, mulDivRound, toSafeNumber } from './rounding'
import { formatQty, type Milli } from './qty'

// A product in two units (docs/plans/two-units.md). Its stock is counted in one of them, which
// never changes; the other is worth a stored share of it, so both cases share these sums.

export type UnitChoice = 'larger' | 'smaller'

export interface TwoUnits {
  larger_name: string
  smaller_name: string
  /** How many smaller in one larger. */
  count: number
  stock_in: UnitChoice
}

export const unitName = (u: TwoUnits, which: UnitChoice) =>
  which === 'larger' ? u.larger_name : u.smaller_name

/** A quantity typed in `which`, in the unit stock is counted in. */
export function toStockQty(qtyMilli: Milli, which: UnitChoice, u: TwoUnits): Milli {
  if (which === u.stock_in) return qtyMilli
  return which === 'larger'
    ? qtyMilli * u.count
    : toSafeNumber(divRound(BigInt(qtyMilli), BigInt(u.count)))
}

/** A price per one of `which`, per one of the unit stock is counted in. */
export function toStockRate(ratePaisa: number, which: UnitChoice, u: TwoUnits): number {
  if (which === u.stock_in) return ratePaisa
  return which === 'larger' ? mulDivRound(ratePaisa, 1, u.count) : ratePaisa * u.count
}

/** What the other unit costs when no price is set for it. */
export function defaultOtherPrice(stockPricePaisa: number, u: TwoUnits): number {
  return u.stock_in === 'larger'
    ? mulDivRound(stockPricePaisa, 1, u.count)
    : stockPricePaisa * u.count
}

/** Stock as whole larger units and the smaller ones left, to the nearest smaller one. */
export function splitStock(milli: Milli, u: TwoUnits): { larger: number; smaller: number } {
  const pieces = mulDivRound(Math.abs(milli), u.stock_in === 'larger' ? u.count : 1, 1000)
  const sign = milli < 0 ? -1 : 1
  return { larger: sign * Math.floor(pieces / u.count), smaller: sign * (pieces % u.count) }
}

/** "1 Strip", "2 Strips", "1.5 Strips". */
export function unitWords(qtyMilli: Milli, name: string): string {
  const plural = Math.abs(qtyMilli) !== 1000 && !/s$/i.test(name)
  const end = /(x|ch|sh)$/i.test(name) ? 'es' : 's'
  return `${formatQty(qtyMilli)} ${name}${plural ? end : ''}`
}

/** "9 Strips 15 Capsules"; a part that is zero is left out. */
export function stockWords(milli: Milli, u: TwoUnits): string {
  const { larger, smaller } = splitStock(milli, u)
  const parts = [
    larger !== 0 ? unitWords(larger * 1000, u.larger_name) : null,
    smaller !== 0 ? unitWords(smaller * 1000, u.smaller_name) : null,
  ].filter(Boolean)
  return parts.join(' ') || unitWords(0, u.larger_name)
}

export type SoldLine = {
  qty_milli: number
  unit_price_paisa: number
  gross_paisa?: number
  discount_paisa: number
  sold_unit?: string | null
  sold_qty_milli?: number | null
}

/** The quantity as it was sold: "5 Capsules"; an ordinary line, its number. */
export function lineQtyText(l: SoldLine): string {
  return l.sold_unit && l.sold_qty_milli
    ? unitWords(l.sold_qty_milli, l.sold_unit)
    : formatQty(l.qty_milli)
}

/** The rate as it was sold: per capsule, though the line holds it per strip. */
export function lineRate(l: SoldLine): number {
  return l.sold_qty_milli
    ? mulDivRound(l.unit_price_paisa, l.qty_milli, l.sold_qty_milli)
    : l.unit_price_paisa
}

/** The discount a person gave: not what made a loose price come out exact. */
export function lineDiscount(l: SoldLine): number {
  if (!l.sold_qty_milli) return l.discount_paisa
  const gross = l.gross_paisa ?? mulDivRound(l.qty_milli, l.unit_price_paisa, 1000)
  return Math.max(0, mulDivRound(l.sold_qty_milli, lineRate(l), 1000) - (gross - l.discount_paisa))
}
